import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { useSettings } from '@/context/SettingsContext';
import { MapPin, Phone, Mail, Clock, Info, Send } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, currentLanguage } = useLanguage();
  const { settings } = useSettings();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  // Settings values
  const phone = settings.phone || '+90 252 382 87 00';
  const email = settings.email || 'info@hirbodrum.com';
  const reservationEmail = settings.reservation_email || 'reservation@hirbodrum.com';
  const addressText = settings.address?.[currentLanguage] || settings.address?.tr || 'Turgutreis, Bahçelievler Mah. Zeyyat Mandalinci Cad. No:164, Bodrum / Muğla';
  const mapEmbed = settings.google_maps_embed || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d50324.55766594!2d27.2196!3d37.0267!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14be6b70f5555555%3A0x5555555555555555!2sTurgutreis%2C%20Bodrum!5e0!3m2!1sen!2str!4v1234567890';

  const phoneClean = phone.replace(/[^0-9+]/g, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language: currentLanguage
      });
      toast.success(t('contact.form.success'));
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const infoItems = [
    { icon: Clock, label: t('contact.checkIn'), value: t('contact.checkInValue') },
    { icon: Clock, label: t('contact.checkOut'), value: t('contact.checkOutValue') },
    { icon: Info, label: t('contact.pets'), value: t('contact.petsValue') },
    { icon: Info, label: t('contact.wristband'), value: t('contact.wristbandValue') },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section
        className="relative h-[40vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `url('/images/general/hero_bg.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-holiday-purple/60" />
        <div className="relative z-10 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-4">{t('contact.title')}</h1>
          <p className="text-xl text-white/80">{t('contact.subtitle')}</p>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-12 bg-white" data-testid="info-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {infoItems.map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 p-6 rounded-lg text-center"
                data-testid={`info-${index}`}
              >
                <item.icon className="w-6 h-6 text-holiday-purple mx-auto mb-2" />
                <h4 className="font-semibold text-gray-900">{item.label}</h4>
                <p className="text-sm text-gray-600">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-padding bg-gray-50" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h2 className="text-3xl font-heading font-bold text-gray-900 mb-6">
                {t('contact.form.title')}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.name')} *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full"
                    data-testid="contact-name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.email')} *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.form.phone')}
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full"
                      data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.subject')} *
                  </label>
                  <Input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full"
                    data-testid="contact-subject"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.message')} *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-holiday-purple focus:ring-1 focus:ring-holiday-purple/20"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <div className="loading-spinner w-5 h-5" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t('contact.form.send')}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Map & Contact Info */}
            <div>
              <div className="bg-white rounded-lg overflow-hidden shadow-lg mb-8">
                <iframe
                  src={mapEmbed}
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Holiday Inn Resort Bodrum Location"
                  data-testid="contact-map"
                />
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-holiday-purple/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-holiday-purple" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('location.address')}</h4>
                    <p className="text-gray-600 whitespace-pre-line">{addressText}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-holiday-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-holiday-green" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Telefon</h4>
                    <a href={`tel:${phoneClean}`} className="text-gray-600 hover:text-holiday-purple">
                      {phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-holiday-purple/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-holiday-purple" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">E-posta</h4>
                    <a href={`mailto:${email}`} className="text-gray-600 hover:text-holiday-purple block">
                      {email}
                    </a>
                    {reservationEmail && (
                      <a href={`mailto:${reservationEmail}`} className="text-gray-600 hover:text-holiday-purple block">
                        {reservationEmail}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
